import {
  calculateCircularProgressOffset,
  calculateCircumference,
  calculatePercentageLeft,
  calculateTimeLeftText,
  calculateTimelineText,
  isCompleted,
} from 'fitbeat.gym-core';
import {
  IUserLeaderboardViewModel, IUserWeightedLeaderboardBreakdown, LeaderboardFrequency, LeaderboardMetric,
} from 'fitbeat.models';
import React, { useState } from 'react';
import {
  Animated, Easing, StyleSheet, Text, TouchableOpacity, View,
} from 'react-native';
import { moderateScale } from 'react-native-size-matters';
import Svg, { Circle, G } from 'react-native-svg';
import arrowIcon from '../../assets/icons/arrow.svg';
import infoIcon from '../../assets/icons/info.svg';
import medalIcon from '../../assets/icons/medal.svg';
import { TranslateProp } from '../../common-types';
import { Icon } from '../../components';
import Label from '../../components/Label';
import { JoinLeaderboard } from '../../Leaderboards/JoinLeaderboard';
import {
  determineScoreBreakdownOverviewHeaders,
  determineTeamsOverviewHeaders,
  leaderboardMetricInfo,
} from '../../Leaderboards/LeaderboardUIHelper';
import Theme from '../../theme';
import LeaderboardOverviewInfo from '../../Tracking/components/leaderboards/LeaderboardOverviewInfo';
import LeaderboardOverviewPosition from '../../Tracking/components/leaderboards/LeaderboardOverviewPosition';
import { SVGIcon } from '../../ui/components';
import { defaultScale, scaleText } from '../../utils/scaling';
import { capitalize } from '../../utils/string-manipulation';

interface IProps {
  userLeaderboardData: IUserLeaderboardViewModel;
  showIcons?: boolean;
  showDivider?: boolean;
  allowPress: boolean;
  displayedOnDashboard?: boolean;
  teamMembersOverview?: boolean;
  isFeatured: boolean;
  showJoinLeaderboardTile: boolean;
  isTeamLeaderboard: boolean;
  userBreakdown: IUserWeightedLeaderboardBreakdown[] | undefined;
  t(key: string, options?: { [key: string]: string }): string;
}

export const leaderboardSubHeading = (
  userLeaderboardData: IUserLeaderboardViewModel,
  t: TranslateProp,
): string => {
  const subheading: string[] = [];
  if (userLeaderboardData.competitionType === 'inter-team') {
    subheading.push(t('tracking:challengeSubHeadingInterTeam'));
  }

  const metricTranslationMap: { [metric in LeaderboardMetric]: string } = {
    'effort-points': t('tracking:challengeSubheadingEffortPoints'),
    'total-minutes': t('tracking:challengeSubheadingWorkoutMins'),
    'average-minutes': t('tracking:challengeSubheadingAverageWorkoutMins'),
    'team-weighted-minutes-score': t('tracking:challengeSubheadingTeamWeightedMins'),
    'team-weighted-points-score': t('tracking:challengeSubheadingTeamWeightedPoints'),
  };

  const metricTranslationOrDefault = metricTranslationMap[userLeaderboardData.metric] || userLeaderboardData.metric;

  subheading.push(metricTranslationOrDefault);
  return capitalize(subheading.join(' '));
};

export function LeaderboardOverview(
  {
    userLeaderboardData,
    t,
    showIcons = true,
    showDivider = false,
    allowPress,
    displayedOnDashboard = false,
    teamMembersOverview = false,
    isFeatured,
    showJoinLeaderboardTile,
    isTeamLeaderboard = false,
    userBreakdown,
  }: IProps,
) {
  const radius = moderateScale(25);
  const strokeWidth = moderateScale(10);
  const newLeaderboard = userLeaderboardData.metric.includes('weighted');
  const showTeamBreakdown = newLeaderboard && !displayedOnDashboard && !isTeamLeaderboard;
  const [isInfoVisible, setIsInfoVisible] = useState(false);

  const percentageCompleteAnimatedValue = React.useRef(new Animated.Value(0)).current;
  const completedCircleRef = React.useRef();
  const circumference = calculateCircumference(radius);
  const halfCircle = radius + strokeWidth;

  const animation = (toValue, animatedValue) => Animated.timing(animatedValue, {
    delay: 500,
    toValue,
    duration: 800,
    useNativeDriver: true,
    easing: Easing.out(Easing.ease),
  }).start();

  React.useEffect(() => {
    animation(
      calculatePercentageLeft(userLeaderboardData.startDate, userLeaderboardData.endDate),
      percentageCompleteAnimatedValue,
    );
    percentageCompleteAnimatedValue.addListener((v) => {
      const strokeDashoffset = calculateCircularProgressOffset(v.value, circumference);

      if (completedCircleRef?.current) {
        completedCircleRef.current.setNativeProps({
          strokeDashoffset,
        });
      }
    });

    return () => {
      percentageCompleteAnimatedValue.removeAllListeners();
    };
  }, [calculatePercentageLeft(userLeaderboardData.startDate, userLeaderboardData.endDate)]);

  const { unit: timeLeftUnit, timeUnit: timeLeftText, isUpcoming } = calculateTimeLeftText(
    userLeaderboardData.startDate,
    userLeaderboardData.endDate,
  );

  const infoToggle = () => {
    setIsInfoVisible(!isInfoVisible);
  };

  const frequencyTranslationMap: { [frequency in LeaderboardFrequency] } = {
    ['daily']: t('tracking:challengeFrequencyDaily'),
    ['weekly']: t('tracking:challengeFrequencyWeekly'),
    ['monthly']: t('tracking:challengeFrequencyMonthly'),
    ['one-time']: '',
  };

  if (showJoinLeaderboardTile) {
    return (
      <JoinLeaderboard
        leaderboardData={userLeaderboardData}
        t={t}
        canDrillIn={allowPress}
        displayedOnDashboard={displayedOnDashboard}
      />
    );
  }

  const chevronDown = {
    ...styles.chevronIcon,
    ...styles.chevronDown,
  };
  const chevronUp = {
    ...styles.chevronIcon,
    ...styles.chevronUp,
  };

  const renderLeaderboardBlurb = () => {
    const bulletPoint = leaderboardMetricInfo(userLeaderboardData, t);
    if (!newLeaderboard) {
      return (
        <Text style={styles.subHeader}>{leaderboardSubHeading(userLeaderboardData, t)}</Text>
      );
    }
    return (
      <TouchableOpacity style={styles.blurbContainer} onPress={infoToggle}>
        <View style={styles.blurbHeader}>
          <SVGIcon
            Icon={infoIcon}
            color={Theme.PRIMARY}
            style={styles.infoIcon}
          />
          <Text style={styles.subHeader}>{t('tracking:leaderboardBlurbHeader')}</Text>
          <View style={{ flex: 1, alignItems: 'flex-end' }}>
            <SVGIcon
              Icon={arrowIcon}
              color={Theme.PRIMARY}
              style={isInfoVisible ? chevronUp : chevronDown}
            />
          </View>
        </View>
        {isInfoVisible
          && (
            <View style={{ marginTop: defaultScale(10) }}>
              {bulletPoint.map((point, index) => (
                <View key={index} style={styles.bulletItem}>
                  <Text style={styles.bulletPoint}>•</Text>
                  <Text style={styles.bulletText}>
                    <Text style={styles.bulletHeaderText}>{point.header}</Text>
                    {point.text}
                  </Text>
                </View>
              ))}
            </View>
          )}
      </TouchableOpacity>
    );
  };

  const renderOverviewHeader = () => {
    if (!showTeamBreakdown) {
      return (
        <View style={styles.headerContainer}>
          <Text style={styles.header}>{userLeaderboardData.name}</Text>
          {allowPress && (
            <View style={styles.arrowContainer}>
              <Icon
                size={defaultScale(18)}
                color={Theme.GREY_DARK_MEDIUM}
                name='ios-arrow-forward'
              />
            </View>
          )}
        </View>
      );
    }
    return (
      <></>
    );
  };

  const renderOverviewTags = () => {
    if (!showTeamBreakdown) {
      return (
        <>
          {isFeatured && (
            <View style={{ paddingRight: defaultScale(5) }}>
              <Label
                text={t('tracking:featured')}
              />
            </View>
          )}
          {userLeaderboardData.frequency !== 'one-time' && (
            <View style={{ paddingRight: defaultScale(5) }}>
              <Label
                containerStyle={{ backgroundColor: Theme.GREY_LIGHT }}
                text={frequencyTranslationMap[userLeaderboardData.frequency]}
                textStyle={{ color: Theme.GREY_DARK_MEDIUM }}
              />
            </View>
          )}
        </>
      );
    }
    return (
      <></>
    );
  };

  const renderRankingAndProgress = () => {
    if (userBreakdown) {
      const overviewInfo = determineScoreBreakdownOverviewHeaders(userBreakdown, t, userLeaderboardData.metric);
      return (
        <View style={styles.breakdownRow}>
          {overviewInfo.map((info, index) => (
            <View style={styles.breakdownOverviewContainer} key={index}>
              {index !== 0 && (
                <View style={styles.breakdownDivider} />
              )}
              <LeaderboardOverviewInfo
                header={info.header}
                subHeader={info.subHeader}
              />
            </View>
          ))}
        </View>
      );
    }
    if (showTeamBreakdown) {
      const overviewInfo = determineTeamsOverviewHeaders(userLeaderboardData, t);
      return (
        <View style={[styles.row, { marginTop: defaultScale(-10) }]}>
          <View style={{ flexDirection: 'row', flex: 1 }}>
            <LeaderboardOverviewInfo
              header={overviewInfo[0].header}
              subHeader={overviewInfo[0].subHeader}
            />
          </View>
          <View style={{ flexDirection: 'row', flex: 1 }}>
            {showDivider && <View style={styles.horizontalDivider} />}
            <LeaderboardOverviewInfo
              header={overviewInfo[1].header}
              subHeader={overviewInfo[1].subHeader}
            />
          </View>
        </View>
      );
    }
    return (
      <View style={[styles.row, { marginTop: !newLeaderboard ? defaultScale(20) : defaultScale(10) }]}>
        <View style={{ flexDirection: 'row', flex: 1 }}>
          {showIcons && (
            <View style={{ paddingRight: 5 }}>
              <SVGIcon
                Icon={medalIcon}
                color={Theme.PRIMARY}
                style={{ height: defaultScale(55), width: defaultScale(46) }}
              />
            </View>
          )}
          <LeaderboardOverviewPosition leaderboard={userLeaderboardData} teamPosition={teamMembersOverview} t={t} />
        </View>
        <View style={{ flexDirection: 'row', flex: 1 }}>
          {showDivider && <View style={styles.horizontalDivider} />}

          {showIcons && (
            <View style={{ paddingRight: 5 }}>
              <Svg
                height={radius * 2}
                width={radius * 2}
                viewBox={`0 0 ${halfCircle * 2} ${halfCircle * 2}`}
              >
                <G
                  rotation='-90'
                  origin={`${halfCircle}, ${halfCircle}`}
                >
                  <Circle
                    cx='50%'
                    cy='50%'
                    r={radius}
                    fill='transparent'
                    stroke={Theme.GREY_LIGHT}
                    strokeWidth={strokeWidth}
                    strokeLinejoin='round'
                  />
                  <Circle
                    ref={completedCircleRef}
                    cx='50%'
                    cy='50%'
                    r={radius}
                    fill='transparent'
                    stroke={Theme.PRIMARY}
                    strokeWidth={strokeWidth}
                    strokeDashoffset={circumference}
                    strokeDasharray={circumference}
                  />
                </G>
              </Svg>
            </View>
          )}
          <View>
            {isCompleted(new Date(userLeaderboardData.endDate))
              && (
                <View style={styles.textContainer}>
                  <Text style={[styles.lightText, { fontFamily: Theme.Weight700 }]}>{t('tracking:completed')}</Text>
                </View>
              )}

            {!isCompleted(new Date(userLeaderboardData.endDate))
              && (
                <View style={styles.textContainer}>
                  <Text style={styles.weightText}>
                    {`${timeLeftUnit}`}
                  </Text>
                  <Text style={styles.lightText}>
                    {` ${timeLeftText} ${isUpcoming ? 'until start' : 'left'}`}
                  </Text>
                </View>
              )}
            <Text style={styles.subText}>
              {`${calculateTimelineText(
                userLeaderboardData.startDate,
                userLeaderboardData.endDate,
                userLeaderboardData.timezone,
                !displayedOnDashboard,
              )}`}
            </Text>
          </View>
        </View>
      </View>
    );
  };

  const renderLeaderboardOverview = () => {
    if (!newLeaderboard) {
      return (
        <View>
          {renderOverviewHeader()}
          {!displayedOnDashboard && (
            <View style={styles.subHeaderContainer}>
              {renderOverviewTags()}
              {renderLeaderboardBlurb()}
            </View>
          )}
        </View>
      );
    }
    return (
      <View>
        {!displayedOnDashboard && (
          <View style={[styles.subHeaderContainer, { marginBottom: defaultScale(10) }]}>
            {renderOverviewTags()}
          </View>
        )}
        {renderOverviewHeader()}
      </View>
    );
  };

  return (
    <View style={styles.container}>
      {renderLeaderboardOverview()}
      {renderRankingAndProgress()}
      {newLeaderboard && !displayedOnDashboard && (
        renderLeaderboardBlurb()
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  header: {
    ...Theme.textStyle.heading,
    flex: 1,
    marginRight: defaultScale(30),
  },
  subHeader: {
    ...Theme.textStyle.bodyCopyReg,
  },
  blurbSubHeader: {
    ...Theme.textStyle.bodyCopyReg,
  },
  subHeaderContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: defaultScale(10),
  },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  weightText: {
    fontFamily: Theme.Weight700,
    fontSize: scaleText(24),
    color: Theme.BLACK,
  },
  lightText: {
    display: 'flex',
    fontFamily: Theme.Weight500,
    fontSize: scaleText(16),
    color: Theme.GREY,
    alignSelf: 'flex-end',
    paddingBottom: defaultScale(2),
  },
  textContainer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
  },
  row: {
    flexDirection: 'row',
    marginTop: defaultScale(20),
    paddingRight: defaultScale(10),
  },
  subText: {
    ...Theme.textStyle.small,
    marginTop: 2,
    width: defaultScale(160),
  },
  arrowContainer: {
    alignSelf: 'center',
  },
  container: {
    padding: defaultScale(18),
    backgroundColor: Theme.WHITE,
  },
  horizontalDivider: {
    borderLeftWidth: 1,
    borderLeftColor: Theme.GREY_LIGHT,
    marginRight: defaultScale(22),
  },
  blurbContainer: {
    marginTop: defaultScale(10),
    borderRadius: defaultScale(10),
    paddingVertical: defaultScale(10),
    paddingHorizontal: defaultScale(12),
    backgroundColor: Theme.GREY_LIGHT,
  },
  blurbHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  infoIcon: {
    height: defaultScale(25),
    width: defaultScale(25),
    marginRight: defaultScale(10),
  },
  chevronIcon: {
    height: defaultScale(16),
    width: defaultScale(18),
    marginLeft: defaultScale(10),
  },
  chevronDown: {
    transform: [{
      rotate: '90deg',
    }],
  },
  chevronUp: {
    transform: [{
      rotate: '270deg',
    }],
  },
  infoText: {
    ...Theme.textStyle.bodyCopyReg,
    color: Theme.GREY_DARK,
    marginLeft: defaultScale(5),
  },
  boldText: {
    ...Theme.textStyle.bodyCopyReg,
    fontWeight: 'bold',
    marginTop: defaultScale(10),
  },
  bulletItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: defaultScale(12),
    marginLeft: defaultScale(8),
  },
  bulletPoint: {
    marginRight: defaultScale(5),
  },
  bulletText: {
    ...Theme.textStyle.bodyCopyReg,
    color: Theme.GREY_DARK,
    flex: 1,
  },
  bulletHeaderText: {
    ...Theme.textStyle.bodyCopyBold,
  },
  breakdownOverviewContainer: {
    flexDirection: 'row',
    flex: 1,
  },
  breakdownRow: {
    flexDirection: 'row',
    marginLeft: defaultScale(18),
    marginTop: defaultScale(-20),
  },
  breakdownContainer: {
    backgroundColor: Theme.WHITE,
    marginBottom: defaultScale(10),
    padding: defaultScale(18),
  },
  breakdownDivider: {
    borderLeftWidth: 1,
    borderLeftColor: Theme.GREY_LIGHT,
    marginRight: defaultScale(10),
  },
});
